import React, { useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { MapPin, Globe } from 'lucide-react';

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

interface LogsMapProps {
    countries: { country: string; total: number; cities: { city: string; count: number }[] }[];
    className?: string;
}

interface TooltipData {
    name: string;
    code?: string;
    visits: number;
    x: number;
    y: number;
}

// Helper to get flag URL
const getFlagUrl = (code?: string) =>
    code ? `https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/${code.toLowerCase()}.svg` : '';

// ISO2 to Numeric Code (M49) mapping for world-atlas/countries-110m.json
const iso2numeric: Record<string, string> = {
    'AF': '004', 'AX': '248', 'AL': '008', 'DZ': '012', 'AS': '016', 'AD': '020', 'AO': '024',
    'AI': '660', 'AQ': '010', 'AG': '028', 'AR': '032', 'AM': '051', 'AW': '533', 'AU': '036',
    'AT': '040', 'AZ': '031', 'BS': '044', 'BH': '048', 'BD': '050', 'BB': '052', 'BY': '112',
    'BE': '056', 'BZ': '084', 'BJ': '204', 'BM': '060', 'BT': '064', 'BO': '068', 'BQ': '535',
    'BA': '070', 'BW': '072', 'BV': '074', 'BR': '076', 'IO': '086', 'BN': '096', 'BG': '100',
    'BF': '854', 'BI': '108', 'CV': '132', 'KH': '116', 'CM': '120', 'CA': '124', 'KY': '136',
    'CF': '140', 'TD': '148', 'CL': '152', 'CN': '156', 'CX': '162', 'CC': '166', 'CO': '170',
    'KM': '174', 'CG': '178', 'CD': '180', 'CK': '184', 'CR': '188', 'CI': '384', 'HR': '191',
    'CU': '192', 'CW': '531', 'CY': '196', 'CZ': '203', 'DK': '208', 'DJ': '262', 'DM': '212',
    'DO': '214', 'EC': '218', 'EG': '818', 'SV': '222', 'GQ': '226', 'ER': '232', 'EE': '233',
    'ET': '231', 'FK': '238', 'FO': '234', 'FJ': '242', 'FI': '246', 'FR': '250', 'GF': '254',
    'PF': '258', 'TF': '260', 'GA': '266', 'GM': '270', 'GE': '268', 'DE': '276', 'GH': '288',
    'GI': '292', 'GR': '300', 'GL': '304', 'GD': '308', 'GP': '312', 'GU': '316', 'GT': '320',
    'GG': '831', 'GN': '324', 'GW': '624', 'GY': '328', 'HT': '332', 'HM': '334', 'VA': '336',
    'HN': '340', 'HK': '344', 'HU': '348', 'IS': '352', 'IN': '356', 'ID': '360', 'IR': '364',
    'IQ': '368', 'IE': '372', 'IM': '833', 'IL': '376', 'IT': '380', 'JM': '388', 'JP': '392',
    'JE': '832', 'JO': '400', 'KZ': '398', 'KE': '404', 'KI': '296', 'KP': '408', 'KR': '410',
    'KW': '414', 'KG': '417', 'LA': '418', 'LV': '428', 'LB': '422', 'LS': '426', 'LR': '430',
    'LY': '434', 'LI': '438', 'LT': '440', 'LU': '442', 'MO': '446', 'MK': '807', 'MG': '450',
    'MW': '454', 'MY': '458', 'MV': '462', 'ML': '466', 'MT': '470', 'MH': '584', 'MQ': '474',
    'MR': '478', 'MU': '480', 'YT': '175', 'MX': '484', 'FM': '583', 'MD': '498', 'MC': '492',
    'MN': '496', 'ME': '499', 'MS': '500', 'MA': '504', 'MZ': '508', 'MM': '104', 'NA': '516',
    'NR': '520', 'NP': '524', 'NL': '528', 'NC': '540', 'NZ': '554', 'NI': '558', 'NE': '562',
    'NG': '566', 'NU': '570', 'NF': '574', 'MP': '580', 'NO': '578', 'OM': '512', 'PK': '586',
    'PW': '585', 'PS': '275', 'PA': '591', 'PG': '598', 'PY': '600', 'PE': '604', 'PH': '608',
    'PN': '612', 'PL': '616', 'PT': '620', 'PR': '630', 'QA': '634', 'RE': '638', 'RO': '642',
    'RU': '643', 'RW': '646', 'BL': '652', 'SH': '654', 'KN': '659', 'LC': '662', 'MF': '663',
    'PM': '666', 'VC': '670', 'WS': '882', 'SM': '674', 'ST': '678', 'SA': '682', 'SN': '686',
    'RS': '688', 'SC': '690', 'SL': '694', 'SG': '702', 'SX': '534', 'SK': '703', 'SI': '705',
    'SB': '090', 'SO': '706', 'ZA': '710', 'GS': '239', 'SS': '728', 'ES': '724', 'LK': '144',
    'SD': '729', 'SR': '740', 'SJ': '744', 'SZ': '748', 'SE': '752', 'CH': '756', 'SY': '760',
    'TW': '158', 'TJ': '762', 'TZ': '834', 'TH': '764', 'TL': '626', 'TG': '768', 'TK': '772',
    'TO': '776', 'TT': '780', 'TN': '788', 'TR': '792', 'TM': '795', 'TC': '796', 'TV': '798',
    'UG': '800', 'UA': '804', 'AE': '784', 'GB': '826', 'US': '840', 'UM': '581', 'UY': '858',
    'UZ': '860', 'VU': '548', 'VE': '862', 'VN': '704', 'VG': '092', 'VI': '850', 'WF': '876',
    'EH': '732', 'YE': '887', 'ZM': '894', 'ZW': '716'
};

const LogsMap: React.FC<LogsMapProps> = ({ countries, className = '' }) => {
    const [tooltip, setTooltip] = useState<TooltipData | null>(null);

    // Calculate max for scaling
    const maxVisits = Math.max(...countries.map(c => c.total), 1);

    return (
        <div className={`relative w-full h-full ${className}`}>
            <div className="absolute inset-0 rounded-lg">{/* Transparent background, no gradient */}
                <ComposableMap
                    projectionConfig={{
                        scale: 230,
                        center: [10, 10]
                    }}
                    style={{
                        width: '100%',
                        height: '100%'
                    }}
                >
                    <Geographies geography={geoUrl}>
                        {({ geographies }: { geographies: any[] }) =>
                            geographies.map((geo: any) => {
                                // Try to match using various methods
                                const countryData = countries.find(c => {
                                    const code = c.country.toUpperCase();
                                    const numeric = iso2numeric[code];

                                    // Try to get English name for exact match
                                    let englishName = '';
                                    try {
                                        englishName = new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || '';
                                    } catch (e) { }

                                    // Match by Numeric Code (geo.id) or Exact Name
                                    // NO startsWith fallback to avoid Finland -> Fiji
                                    return (numeric && numeric === geo.id) ||
                                        (geo.properties.ISO_A2 && geo.properties.ISO_A2.toUpperCase() === code) ||
                                        (geo.properties.name && geo.properties.name === englishName);
                                });

                                const hasVisits = !!countryData;
                                const visitPercent = countryData ? (countryData.total / maxVisits) : 0;

                                return (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        onMouseEnter={(e) => {
                                            if (countryData) {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setTooltip({
                                                    name: geo.properties.name || geo.id,
                                                    code: countryData ? countryData.country : undefined,
                                                    visits: countryData.total,
                                                    x: rect.left + rect.width / 2,
                                                    y: rect.top
                                                });
                                            }
                                        }}
                                        onMouseLeave={() => setTooltip(null)}
                                        style={{
                                            default: {
                                                fill: hasVisits
                                                    ? `rgba(var(--accent-primary-rgb), ${0.4 + visitPercent * 0.6})`
                                                    : 'rgba(255, 255, 255, 0.03)',
                                                stroke: 'transparent',
                                                strokeWidth: 0,
                                                outline: 'none'
                                            },
                                            hover: {
                                                fill: hasVisits
                                                    ? `rgba(var(--accent-primary-rgb), ${0.6 + visitPercent * 0.4})`
                                                    : 'rgba(255, 255, 255, 0.05)',
                                                stroke: 'transparent',
                                                strokeWidth: 0,
                                                cursor: hasVisits ? 'pointer' : 'default',
                                                outline: 'none'
                                            },
                                            pressed: {
                                                fill: hasVisits ? 'rgba(var(--accent-primary-rgb), 0.8)' : 'rgba(255, 255, 255, 0.05)',
                                                outline: 'none'
                                            }
                                        }}
                                    />
                                );
                            })
                        }
                    </Geographies>
                </ComposableMap>

                {/* Subtle Grid Overlay */}
            </div>

            {/* Tooltip */}
            {tooltip && (
                <div
                    className="fixed z-50 pointer-events-none"
                    style={{
                        left: tooltip.x,
                        top: tooltip.y - 10,
                        transform: 'translate(-50%, -100%)'
                    }}
                >
                    <div className="bg-[#151515] rounded-xl p-3 flex items-center gap-3 shadow-[0_8px_30px_rgb(0,0,0,0.5)] min-w-[150px]">
                        <div className="relative w-10 h-10 flex-shrink-0">
                            {tooltip.code ? (
                                <img
                                    src={getFlagUrl(tooltip.code)}
                                    className="w-full h-full object-cover rounded-full"
                                    alt={tooltip.name}
                                />
                            ) : (
                                <div className="w-full h-full bg-white/5 rounded-full flex items-center justify-center">
                                    <Globe className="w-5 h-5 text-white/40" />
                                </div>
                            )}
                            <div className="absolute -bottom-1 -right-1 bg-[rgb(var(--accent-primary-rgb))] rounded-full p-1 shadow-sm">
                                <MapPin className="w-2.5 h-2.5 text-white" />
                            </div>
                        </div>

                        <div>
                            <div className="text-white font-bold text-sm tracking-wide leading-none">{tooltip.name}</div>
                            <div className="flex items-center gap-1.5 mt-1.5">
                                <span className="text-[rgb(var(--accent-primary-rgb))] font-mono text-xs font-bold">{tooltip.visits}</span>
                                <span className="text-white/40 text-[9px] uppercase tracking-wider font-semibold">{tooltip.visits === 1 ? 'VISITOR' : 'VISITORS'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Arrow blended with background */}
                    <div
                        className="absolute left-1/2 -translate-x-1/2 w-0 h-0 translate-y-[-1px]"
                        style={{
                            borderLeft: '8px solid transparent',
                            borderRight: '8px solid transparent',
                            borderTop: '8px solid #151515'
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default LogsMap;
